import { api } from './api';
import type {
  AuthResponse,
  ChangePasswordPayload,
  ForgotPasswordPayload,
  LoginCredentials,
  MagicLinkRequestPayload,
  RefreshTokenPayload,
  ResetPasswordPayload,
  User,
} from '../types/auth.types';

// Re-export types for backward compatibility
export type { AuthResponse, User } from '../types/auth.types';

export interface RegisterDto {
  name: string;
  email: string;
  password: string;
  role?: 'admin' | 'manager' | 'sales' | 'viewer';
}

/**
 * Auth Service — API calls for authentication
 */
export const authService = {
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const { data } = await api.post<AuthResponse>('/auth/login', credentials);
    return data;
  },

  async register(userData: RegisterDto): Promise<AuthResponse> {
    const { data } = await api.post<AuthResponse>('/auth/register', userData);
    return data;
  },

  async getCurrentUser(): Promise<User> {
    const { data } = await api.get<User>('/auth/me');
    return data;
  },

  async refreshToken(payload: RefreshTokenPayload): Promise<AuthResponse> {
    const { data } = await api.post<AuthResponse>('/auth/refresh', payload);
    return data;
  },

  async logout(): Promise<void> {
    try {
      await api.post('/auth/logout');
    } catch {
      // Ignore errors — we clear local state regardless
    }
  },

  async changePassword(payload: ChangePasswordPayload): Promise<void> {
    await api.post('/auth/change-password', payload);
  },

  async forgotPassword(payload: ForgotPasswordPayload): Promise<void> {
    await api.post('/auth/forgot-password', payload);
  },

  async resetPassword(payload: ResetPasswordPayload): Promise<void> {
    await api.post('/auth/reset-password', payload);
  },

  async requestMagicLink(payload: MagicLinkRequestPayload): Promise<void> {
    await api.post('/auth/magic-link/request', payload);
  },

  async verifyMagicLink(token: string): Promise<AuthResponse> {
    const { data } = await api.post<AuthResponse>('/auth/magic-link/verify', { token });
    return data;
  },
};
